import { setUserPreferences } from '../services/userPreferences'

export const saveCategoryOpacity = data => queueJobs(data)
export const saveFavoriteIcons = data => queueJobs(data)
export const saveFavoriteStates = data => queueJobs(data)
export const saveLayers = data => queueJobs(data)
export const savePinnedCollection = data => queueJobs(data)
export const saveRecentStyles = data => queueJobs(data)
export const saveTerraiAnalysys = data => queueJobs(data)
export const saveVisibleFeatures = data => queueJobs(data)

/* Constants */
const FLUSH_INTERVAL_MS = 3 * 60 * 1000 // trailing debounce: flush this long after the last change
const MAX_FLUSH_INTERVAL_MS = 5 * 60 * 1000 // hard cap: never hold changes longer than this, even under continuous activity

/* State variables */
let pendingJobs = [] // array of { action, path } jobs waiting to be flushed
let flushTimer = null // trailing-debounce timer
let firstQueuedAt = null // when the oldest pending job was queued (drives MAX_FLUSH_INTERVAL_MS)

/* Flush on tab hide / unload. visibilitychange('hidden') is the reliable hook on
   mobile — beforeunload/pagehide frequently don't fire when the tab is backgrounded
   or discarded; pagehide covers desktop navigation and bfcache. Both route through
   the beacon, which clears the queue, so whichever fires second is a no-op. */
window.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'hidden') flushWithBeacon()
})
window.addEventListener('pagehide', flushWithBeacon)

/* Helpers */

/* Send queued jobs via sendBeacon (carries session cookies automatically). Used for
   the tab-hide/unload path, where a normal async request may be killed mid-flight. */
function flushWithBeacon() {
  if (!pendingJobs.length) return
  const url = `${window.__env.REACT_APP_USER_API_URL}/user/preferences/maps`
  const jobs = pendingJobs
  clearFlushState()
  const queued = navigator.sendBeacon(url, new Blob([JSON.stringify({ jobs })], { type: 'application/json' }))
  // If the browser refused to queue the beacon, keep the jobs for a later attempt.
  if (!queued) requeue(jobs)
}

/* Flush pending jobs to the server via the normal request path. */
function flushPendingJobs() {
  if (!pendingJobs.length) return
  const jobs = pendingJobs
  clearFlushState()
  // userPreferencesRequest swallows errors and resolves { success: false } (it never
  // rejects), so re-queue on a falsy success instead of relying on .catch.
  setUserPreferences({ path: 'maps', data: { jobs } }).then(res => {
    if (!res?.success) requeue(jobs)
  })
}

/* Put jobs back at the front of the queue (they are the oldest) and reschedule.
   Prepending keeps any jobs queued during the in-flight request behind these. */
function requeue(jobs) {
  pendingJobs = [...jobs, ...pendingJobs]
  if (firstQueuedAt == null) firstQueuedAt = Date.now()
  resetFlushTimer()
}

/* Clear the queue and any armed timer/state. */
function clearFlushState() {
  clearTimeout(flushTimer)
  flushTimer = null
  pendingJobs = []
  firstQueuedAt = null
}

/* (Re)arm the debounce timer, capped so we never wait past MAX_FLUSH_INTERVAL_MS from the first
   pending change — prevents starvation when changes never pause for a full interval. */
function resetFlushTimer() {
  clearTimeout(flushTimer)
  const sinceFirst = firstQueuedAt == null ? 0 : Date.now() - firstQueuedAt
  const wait = Math.min(FLUSH_INTERVAL_MS, Math.max(0, MAX_FLUSH_INTERVAL_MS - sinceFirst))
  flushTimer = setTimeout(flushPendingJobs, wait)
}

/* Queue jobs */
function queueJobs(data) {
  if (!data?.jobs?.length) return
  if (!pendingJobs.length) firstQueuedAt = Date.now()
  pendingJobs.push(...data.jobs)
  resetFlushTimer()
}
